import React, { useState, useRef, useEffect } from 'react';
import { Plus, Cross } from 'shared/icons';
import useOnOutsideClick from 'shared/hooks/onOutsideClick';

import {
  Container,
  Wrapper,
  Placeholder,
  AddIconWrapper,
  AddCardButton,
  ListNameEditor,
  ListAddControls,
  CancelAdd,
  ListNameEditorWrapper,
} from './Styles';

type NameEditorProps = {
  buttonLabel?: string;
  onSave: (listName: string) => void;
  onCancel: () => void;
};

export const NameEditor: React.FC<NameEditorProps> = ({ onSave: handleSave, onCancel, buttonLabel = 'Save' }) => {
  const $editorRef = useRef<HTMLTextAreaElement>(null);
  const [listName, setListName] = useState('');
  useEffect(() => {
    if ($editorRef && $editorRef.current) {
      $editorRef.current.focus();
    }
  });
  const onSave = (newName: string) => {
    if (newName.replace(/\s+/g, '') !== '') {
      handleSave(newName);
      setListName('');
    }
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSave(listName);
      setListName('');
      if ($editorRef && $editorRef.current) {
        $editorRef.current.focus();
      }
    }
  };
  return (
    <>
      <ListNameEditorWrapper>
        <ListNameEditor
          ref={$editorRef}
          onKeyDown={onKeyDown}
          value={listName}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setListName(e.currentTarget.value)}
          placeholder="Enter a name for this project..."
        />
      </ListNameEditorWrapper>
      <ListAddControls>
        <AddCardButton
          variant="relief"
          onClick={() => {
            onSave(listName);
            setListName('');
            if ($editorRef && $editorRef.current) {
              $editorRef.current.focus();
            }
          }}
        >
          {buttonLabel}
        </AddCardButton>
        <CancelAdd onClick={() => onCancel()}>
          <Cross width={16} height={16} />
        </CancelAdd>
      </ListAddControls>
    </>
  );
};

type AddCardProps = {
  onSave: (listName: string) => void;
  label?: string;
};

const AddCard: React.FC<AddCardProps> = ({ onSave, label = 'Add another task' }) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const $wrapperRef = useRef<HTMLDivElement>(null);
  const onOutsideClick = () => {
    setEditorOpen(false);
  };
  useOnOutsideClick($wrapperRef, editorOpen, onOutsideClick, null);

  return (
    <Container>
      <Wrapper
        ref={$wrapperRef}
        editorOpen={editorOpen}
        onClick={() => {
          if (!editorOpen) {
            setEditorOpen(true);
          }
        }}
      >
        {editorOpen ? (
          <NameEditor
            onCancel={() => setEditorOpen(false)}
            onSave={name => {
              onSave(name);
            }}
          />
        ) : (
          <Placeholder>
            <AddIconWrapper>
              <Plus width={12} height={12} />
            </AddIconWrapper>
            {label}
          </Placeholder>
        )}
      </Wrapper>
    </Container>
  );
};

export default AddCard;
